import { Actions } from '@components/shared/Actions'
import { EyeIcon } from '@assets/svgs/EyeIcon'

import type { ColumnDefProps } from '@components/shared/Table/types'
import type { GroupedRowData } from '@components/shared/TableGrouped/types'
import { Checkbox } from '@components/shared/Checkbox'
import { type PreviewsStatusProps } from '@components/shared/BudgetPreviewsStatus/types'
import {
  ArrowUUpLeftIcon,
  BinocularsIcon,
  CheckIcon,
  ChecksIcon,
  InfoIcon,
  NotePencilIcon,
  PaperPlaneTiltIcon
} from '@phosphor-icons/react'
import { Tooltip } from '@components/shared/Tooltip'

import type { ConsolidatedPreviewDetailsItemProps } from '@services/api/preview/types'
import type { SelectConsolidatedPreviewIdProps } from '../hooks/usePreview/types'
import { BudgetPreviewsStatus } from '@components/shared/BudgetPreviewsStatus'
import { currencyMask } from '@utils/masks/currencyMask'

const shouldShowRealizedM1 = (): boolean => {
  const today = new Date()
  const currentDay = today.getDate()
  return currentDay >= 10
}

interface ParseDetailedPreviewGridProps {
  data: ConsolidatedPreviewDetailsItemProps[]
  onSendPreview: (data: any) => void
  onValidatePreview: (data: any) => void
  onReturnPreview: (data: any) => void
  onApprovePreview: (data: any) => void
  onJustifyDeviation: (data: any) => void
  onSeeMore: (data: any) => void
  onSeeMoreReturn: (data: any) => void
  selectedIds?: { id: string; weekNumber: number }[]
  onToggleCheckbox?: (value: SelectConsolidatedPreviewIdProps) => void
  onSelectAll?: (value: boolean) => void
  selectedAllIds?: boolean
  canEdit: boolean
}

const createRowFromItem = (
  item: ConsolidatedPreviewDetailsItemProps,
  onSeeMore: (data: any) => void,
  onSendPreview: (data: any) => void,
  onValidatePreview: (data: any) => void,
  onReturnPreview: (data: any) => void,
  onApprovePreview: (data: any) => void,
  onJustifyDeviation: (data: any) => void,
  onSeeMoreReturn: (data: any) => void,
  selectedIds?: { id: string; weekNumber: number }[],
  onToggleCheckbox?: (value: SelectConsolidatedPreviewIdProps) => void,
  isChild: boolean = false,
  onSelectAll?: (value: boolean) => void,
  selectedAllIds?: boolean,
  canEdit?: boolean
): ColumnDefProps[] => {
  const budget = item.budget
  const budgetPercentage = item.budgetPercentage
  const actualPreview = item.actualPreview
  const actualPreviewPercentage = item.actualPreviewPercentage

  const deviationVsBudget = item.deviationVsBudget
  const deviationVsBudgetPercentage = item.deviationVsBudgetPercentage

  const isGrossProfitRow = (item.account || 'Gross Profit') === 'Gross Profit'

  // A visibilidade de cada ação vem só das flags do item (as roles agora são personalizadas e o
  // backend já resolve a permissão por linha). Duas ações dependem também do tipo de linha:
  // "Ver mais" (não tem flag e só faz sentido na linha de conta, que tem tela de detalhe) e
  // "Justificar Desvio" (nunca na linha de Gross Profit).
  const rowActions = [
    {
      id: 'sendPreview',
      label: 'Enviar Prévia',
      icon: <PaperPlaneTiltIcon />,
      onClick: () => onSendPreview(item),
      visible: item.canSubmit
    },
    {
      id: 'validatePreview',
      label: 'Validar Prévia',
      icon: <CheckIcon />,
      onClick: () => onValidatePreview(item),
      visible: item.canValidate
    },
    {
      id: 'returnPreview',
      label: 'Devolver Prévia',
      icon: <ArrowUUpLeftIcon />,
      onClick: () => onReturnPreview(item),
      visible: item.canReturn
    },
    {
      id: 'approvePreview',
      label: 'Aprovar Prévia',
      icon: <ChecksIcon />,
      onClick: () => onApprovePreview(item),
      visible: item.canApprove
    },
    {
      id: 'justifyDeviation',
      label: 'Justificar Desvio',
      icon: <NotePencilIcon />,
      onClick: () => onJustifyDeviation(item),
      visible: item.canJustify && !isGrossProfitRow
    },
    {
      id: 'seeMore',
      label: 'Ver mais',
      icon: <EyeIcon />,
      onClick: () => onSeeMore(item),
      visible: !isGrossProfitRow
    },
    {
      id: 'seeMoreReturn',
      label: 'Ver detalhes Devolução',
      icon: <BinocularsIcon />,
      onClick: () => onSeeMoreReturn(item),
      visible: item.canSeeMoreReturn
    }
  ].filter((action) => action.visible)

  return [
    ...(canEdit
      ? [
          {
            id: 'id',
            columnName: !isChild ? (
              <Checkbox
                hasController={false}
                name="id"
                label=""
                checked={selectedAllIds}
                onChange={({ target: { checked } }) => onSelectAll?.(checked)}
              />
            ) : null,
            value: isChild
              ? null
              : (() => {
                  const isDisabled = !(item.canSubmit || item.canValidate || item.canReturn || item.canApprove)
                  const checkbox = (
                    <Checkbox
                      hasController={false}
                      name="id"
                      label=""
                      checked={
                        selectedIds?.some(
                          (selectedId) =>
                            selectedId.id === item.profitCenter && selectedId.weekNumber === item.weekNumber
                        ) || selectedAllIds
                      }
                      onChange={({ target }) =>
                        onToggleCheckbox?.({
                          id: item.profitCenter,
                          weekNumber: item.weekNumber,
                          checked: target.checked
                        })
                      }
                      disabled={selectedAllIds || isDisabled}
                    />
                  )
                  if (isDisabled && !selectedAllIds) {
                    return (
                      <Tooltip placement="top" title="Prévia não disponível para ação em massa.">
                        <span style={{ display: 'inline-flex' }}>{checkbox}</span>
                      </Tooltip>
                    )
                  }
                  return checkbox
                })(),
            align: 'center' as const,
            fontWeight: isGrossProfitRow ? 600 : undefined,
            minWidth: '4rem',
            withoutTooltip: true
          }
        ]
      : []),
    {
      id: 'economicGroup',
      columnName: 'Grupo Econômico',
      sortKey: 'economicGroup',
      align: 'center' as const,
      stickyPosition: 'left',
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.economicGroup || '-',
      sortable: true,
      minWidth: '18rem'
    },
    {
      id: 'profitCenter',
      columnName: 'Centro de Lucro',
      sortKey: 'profitCenter',
      align: 'center' as const,
      stickyPosition: 'left',
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.profitCenter || '-',
      sortable: true,
      minWidth: '18rem'
    },
    {
      id: 'account',
      columnName: 'Conta',
      sortKey: 'accountId',
      align: 'center' as const,
      stickyPosition: 'left',
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: (() => {
        const displayValue = item.account || 'Gross Profit'
        return displayValue === 'Gross Profit' ? <span style={{ fontWeight: 600 }}>Gross Profit</span> : displayValue
      })(),
      sortable: true,
      minWidth: '18rem'
    },
    {
      id: 'week',
      columnName: 'Semana',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.weekNumber
        ? `Semana ${item.weekNumber}`
        : item.currentPreviewWeekNumber
          ? `Semana ${item.currentPreviewWeekNumber}`
          : '-',
      minWidth: '13rem'
    },
    {
      id: 'budget',
      columnName: 'Budget (R$)',
      align: 'right' as const,
      headerAlign: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: budget !== null && budget !== undefined ? budget : '-',
      currency: true,
      minWidth: '18rem'
    },
    {
      id: 'budgetPercentage',
      columnName: 'Budget (%)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value:
        budgetPercentage !== null && budgetPercentage !== undefined
          ? `${budgetPercentage.toFixed(2)}%`.replace('.', ',')
          : '-',
      minWidth: '18rem'
    },
    {
      id: 'actualPreview',
      columnName: 'Prévia Atual (R$)',
      align: 'right' as const,
      headerAlign: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: actualPreview !== null && actualPreview !== undefined ? actualPreview : '-',
      currency: true,
      minWidth: '18rem'
    },
    {
      id: 'actualPreviewPercentage',
      columnName: 'Prévia Atual (%)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value:
        actualPreviewPercentage !== null && actualPreviewPercentage !== undefined
          ? `${actualPreviewPercentage.toFixed(2)}%`.replace('.', ',')
          : '-',
      minWidth: '18rem'
    },
    {
      id: 'deviationVsBudget',
      columnName: 'Desvio Budget (R$)',
      align: 'right' as const,
      headerAlign: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: deviationVsBudget,
      currency: true,
      minWidth: '18rem'
    },
    {
      id: 'percentageDeviationVsBudget',
      columnName: 'Desvio Budget (%)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value:
        deviationVsBudgetPercentage !== null && deviationVsBudgetPercentage !== undefined
          ? `${deviationVsBudgetPercentage.toFixed(2)}%`.replace('.', ',')
          : '-',
      minWidth: '18rem'
    },
    {
      id: 'deviationType',
      columnName: (
        <Tooltip
          title="Obrigatório informar o Tipo de Desvio quando: Desvio vs Budget for superior a R$ 50.000,00 ou inferior a -R$ 50.000,00 e % Desvio vs Budget for maior que 5% ou menor que -5%."
          placement="top"
        >
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.25rem', cursor: 'help' }}
          >
            Tipo de Desvio Budget
            <InfoIcon size={16} />
          </div>
        </Tooltip>
      ),
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.deviationType || '-',
      minWidth: '18rem'
    },
    {
      id: 'observations',
      columnName: 'Observações (Desvio Budget)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.observationsBudgetDeviation || '-',
      minWidth: '18rem'
    },
    {
      id: 'deviationVsPreviousWeek',
      columnName: 'Desvio S-1 (R$)',
      align: 'right' as const,
      headerAlign: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.deviationVsS1 || '-',
      currency: true,
      minWidth: '18rem'
    },
    {
      id: 'percentageDeviationVsPreviousWeek',
      columnName: 'Desvio S-1 (%)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.deviationVsS1Percentage ? `${item.deviationVsS1Percentage.toFixed(2)}%`.replace('.', ',') : '-',
      minWidth: '18rem'
    },
    {
      id: 'deviationTypePreviousWeek',
      columnName: (
        <Tooltip
          title="Obrigatório informar o Tipo de Desvio quando: Desvio vs S-1 for superior a R$ 50.000,00 ou inferior a -R$ 50.000,00 e % Desvio vs S-1 for maior que 5% ou menor que -5%."
          placement="top"
        >
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.25rem', cursor: 'help' }}
          >
            Tipo de Desvio S-1
            <InfoIcon size={16} />
          </div>
        </Tooltip>
      ),
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.deviationTypeS1 || '-',
      minWidth: '18rem'
    },
    {
      id: 'observationsPreviousWeek',
      columnName: 'Observações (Desvio S-1)',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: item.observationsS1Deviation || '-',
      minWidth: '18rem'
    },
    {
      id: 'realizedPreviousWeek',
      columnName: (
        <Tooltip title="Dados disponíveis no dia 10, referentes ao mês anterior." placement="top">
          <div
            style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', gap: '0.25rem', cursor: 'help' }}
          >
            Realizado M-1 (R$)
            <InfoIcon size={16} />
          </div>
        </Tooltip>
      ),
      align: 'right' as const,
      headerAlign: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      value: shouldShowRealizedM1()
        ? item.realizedAmountM1 !== null && item.realizedAmountM1 !== undefined
          ? currencyMask(Number(item.realizedAmountM1).toFixed(2))
          : '-'
        : '-',
      minWidth: '18rem'
    },
    {
      id: 'status',
      columnName: 'Status',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      minWidth: '18rem',
      withoutTooltip: true,
      value: item.status ? (
        <BudgetPreviewsStatus status={{ code: item.status as PreviewsStatusProps, descriptionPtbr: '' }} />
      ) : (
        '-'
      )
    },
    {
      id: 'action',
      columnName: 'Ações',
      align: 'center' as const,
      fontWeight: isGrossProfitRow ? 600 : undefined,
      minWidth: '5rem',
      maxWidth: '5rem',
      stickyPosition: 'right',
      withoutTooltip: true,
      // Sem nenhuma ação liberada para a linha, o ícone fica desabilitado em vez de abrir menu vazio.
      value: <Actions actions={rowActions} disabled={rowActions.length === 0} />
    }
  ]
}

const ACCOUNT_ORDER = ['Faturamento', 'Consumo', 'Pessoal', 'Depreciação', 'Outras Despesas', 'Bonificação']

const sortByAccountOrder = (items: ConsolidatedPreviewDetailsItemProps[]): ConsolidatedPreviewDetailsItemProps[] => {
  return [...items].sort((a, b) => {
    const weekA = a.weekNumber ?? a.currentPreviewWeekNumber ?? 0
    const weekB = b.weekNumber ?? b.currentPreviewWeekNumber ?? 0

    if (weekB !== weekA) return weekB - weekA

    const isAGrossProfit = !a.account || a.account === 'Gross Profit'
    const isBGrossProfit = !b.account || b.account === 'Gross Profit'

    if (isAGrossProfit && !isBGrossProfit) return -1
    if (!isAGrossProfit && isBGrossProfit) return 1

    const indexA = ACCOUNT_ORDER.indexOf(a.account ?? '')
    const indexB = ACCOUNT_ORDER.indexOf(b.account ?? '')
    const isAInOrder = indexA !== -1
    const isBInOrder = indexB !== -1

    if (!isAInOrder && !isBInOrder) return (a.account ?? '').localeCompare(b.account ?? '')
    if (!isAInOrder) return 1
    if (!isBInOrder) return -1
    return indexA - indexB
  })
}

const collectAllDescendants = (
  children: ConsolidatedPreviewDetailsItemProps[] | null
): ConsolidatedPreviewDetailsItemProps[] | null => {
  if (!children || children.length === 0) return null
  const result: ConsolidatedPreviewDetailsItemProps[] = []
  for (const child of children) {
    result.push({ ...child, children: null })
    const nested = collectAllDescendants(child.children)
    if (nested?.length) result.push(...nested)
  }
  return result.length ? result : null
}

const flattenChildrenToSecondLevel = (
  items: ConsolidatedPreviewDetailsItemProps[]
): ConsolidatedPreviewDetailsItemProps[] => {
  return items.map((item) => {
    const flatChildren = collectAllDescendants(item.children)
    return { ...item, children: flatChildren ? sortByAccountOrder(flatChildren) : null }
  })
}

const processGroupedData = (
  items: ConsolidatedPreviewDetailsItemProps[],
  onSeeMore: (data: any) => void,
  onSendPreview: (data: any) => void,
  onValidatePreview: (data: any) => void,
  onReturnPreview: (data: any) => void,
  onApprovePreview: (data: any) => void,
  onJustifyDeviation: (data: any) => void,
  onSeeMoreReturn: (data: any) => void,
  selectedIds?: { id: string; weekNumber: number }[],
  onToggleCheckbox?: (value: SelectConsolidatedPreviewIdProps) => void,
  onSelectAll?: (value: boolean) => void,
  parentIndex: number = 0,
  isChildLevel: boolean = false,
  selectedAllIds?: boolean,
  canEdit?: boolean
): GroupedRowData[] => {
  return items.map((item, index) => {
    const hasChildren = item.children !== null && item.children.length > 0
    const uniqueId = `${parentIndex}-${index}-${item.account || item.economicGroup || index}`

    const groupedRow: GroupedRowData = {
      id: uniqueId,
      isParent: hasChildren && !isChildLevel,
      row: createRowFromItem(
        item,
        onSeeMore,
        onSendPreview,
        onValidatePreview,
        onReturnPreview,
        onApprovePreview,
        onJustifyDeviation,
        onSeeMoreReturn,
        selectedIds,
        onToggleCheckbox,
        isChildLevel,
        onSelectAll,
        selectedAllIds,
        canEdit
      )
    }

    if (item.children && hasChildren) {
      groupedRow.children = processGroupedData(
        item.children,
        onSeeMore,
        onSendPreview,
        onValidatePreview,
        onReturnPreview,
        onApprovePreview,
        onJustifyDeviation,
        onSeeMoreReturn,
        selectedIds,
        onToggleCheckbox,
        onSelectAll,
        index,
        true,
        selectedAllIds,
        canEdit
      )
    }

    return groupedRow
  })
}

export const parseDetailedPreviewGrid = ({
  data,
  onSeeMore,
  onSendPreview,
  onValidatePreview,
  onReturnPreview,
  onApprovePreview,
  onJustifyDeviation,
  onSeeMoreReturn,
  selectedIds,
  onToggleCheckbox,
  onSelectAll,
  selectedAllIds,
  canEdit
}: ParseDetailedPreviewGridProps): GroupedRowData[] => {
  if (!data || data.length === 0) return []

  const dataWithFlatChildren = flattenChildrenToSecondLevel(data)

  return processGroupedData(
    dataWithFlatChildren,
    onSeeMore,
    onSendPreview,
    onValidatePreview,
    onReturnPreview,
    onApprovePreview,
    onJustifyDeviation,
    onSeeMoreReturn,
    selectedIds,
    onToggleCheckbox,
    onSelectAll,
    0,
    false,
    selectedAllIds,
    canEdit
  )
}
